import "@xyflow/react/dist/style.css";
import "./custom.css";

import { useRef, useSyncExternalStore } from "react";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";

import { ErrorBoundary } from "@/components/errors/ErrorBoundary";
import { ErrorMaintenance } from "@/components/errors/ErrorMaintenance";
import { UrqlProvider } from "@/components/graphql";
import { GraphqlWsClientProvider } from "@/components/graphql/GraphqlWsContext";
import { LayerThemeProvider } from "@/components/theme";
import { SnackbarProvider } from "@/components/ui-v2/Snackbar";
import { routes } from "@/routes/routes";
import {
  getIsInMaintenance,
  subscribeToMaintenance,
} from "@/utils/maintenance";

import { AppErrorBoundary } from "./components/AppErrorBoundary";
import { AuthProvider } from "./components/AuthProvider";
import { ConfigProvider } from "./components/ConfigProvider";
import { MobileError, useIsMobile } from "./components/mobile";
import { NavigationIndicator } from "./components/NavigationIndicator";
import { Utm } from "./components/Utm";
import { VersionChecker } from "./components/VersionChecker";

const AppContent = () => {
  const routesRef = useRef(
    createBrowserRouter([
      {
        path: "/",
        errorElement: <AppErrorBoundary />,
        element: (
          <QueryParamProvider adapter={ReactRouter6Adapter}>
            <AuthProvider>
              <GraphqlWsClientProvider>
                <UrqlProvider>
                  <SnackbarProvider>
                    <VersionChecker />
                    <NavigationIndicator />
                    <Utm />
                    <Outlet />
                  </SnackbarProvider>
                </UrqlProvider>
              </GraphqlWsClientProvider>
            </AuthProvider>
          </QueryParamProvider>
        ),
        children: routes,
      },
    ]),
  );

  const isMobile = useIsMobile();

  if (isMobile && !import.meta.env.DEV) {
    return <MobileError />;
  }

  return <RouterProvider router={routesRef.current} />;
};

export const App = () => {
  const isInMaintenance = useSyncExternalStore(
    subscribeToMaintenance,
    getIsInMaintenance,
  );

  return (
    <LayerThemeProvider>
      {/* Replaces the whole tree, so nothing behind it keeps sending requests. */}
      {isInMaintenance ? (
        <ErrorMaintenance />
      ) : (
        <ConfigProvider>
          <ErrorBoundary showHelpLinks>
            <AppContent />
          </ErrorBoundary>
        </ConfigProvider>
      )}
    </LayerThemeProvider>
  );
};
